import { Box, Folder, FolderMinus, FolderPlus, Plus, Search, Settings, Star } from "lucide-react";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import { Input } from "../../components/Inputs/Input";
import type { ContextMenuItem } from "../../components/Menu/ContextMenu";
import { Tooltip } from "../../components/Tooltip/Tooltip";
import { usePopoverPosition } from "../../hooks/usePopoverPosition";
import { EmptyNote } from "./EmptyNote";
import { fill, type SidebarFolderHandlers, type SidebarFolderLabels, type SidebarNavSpaceFolder } from "./folderTypes";
import type { SidebarNavSpaceItem } from "./SidebarNav";
import { SidebarSpaceFolders } from "./SidebarSpaceFolders";
import { SidebarSpaceRow } from "./SidebarSpaceRow";

export interface SidebarAllSpacesFlyoutProps {
  isOpen: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  title: string;
  /** "folders" lists the folders and spaces as the sidebar does; "list" is one flat list. Typing in the search is always a flat list. */
  browse: "folders" | "list";
  spaces: SidebarNavSpaceItem[];
  looseSpaces: SidebarNavSpaceItem[];
  folders: SidebarNavSpaceFolder[];
  handlers: SidebarFolderHandlers;
  labels: SidebarFolderLabels;
  activeSpaceKey?: string;
  onOpenSpace: (key: string) => void;
  onTogglePinSpace: (key: string) => void;
  onViewPage?: () => void;
  starLabel: string;
  unpinLabel: string;
  onNewSpace?: () => void;
  newSpaceLabel?: string;
}

/** Every space in one panel beside the sidebar: search, the spaces, and a link to the full page. */
export function SidebarAllSpacesFlyout({
  isOpen,
  anchorRef,
  onClose,
  title,
  browse,
  spaces,
  looseSpaces,
  folders,
  handlers,
  labels,
  activeSpaceKey,
  onOpenSpace,
  onTogglePinSpace,
  onViewPage,
  starLabel,
  unpinLabel,
  onNewSpace,
  newSpaceLabel,
}: Readonly<SidebarAllSpacesFlyoutProps>) {
  const [newFolderRequest, setNewFolderRequest] = useState(0);
  const [query, setQuery] = useState("");
  const { panelRef, style } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef,
    placement: "right",
    align: "start",
  });

  useEffect(() => {
    if (isOpen) setQuery("");
  }, [isOpen]);

  // Escape hands focus back to what opened the panel, so keyboard users don't lose their place.
  const opener = useRef<HTMLElement | null>(null);
  const closedWithEscape = useRef(false);
  useEffect(() => {
    if (!isOpen) return;
    opener.current = document.activeElement as HTMLElement | null;
    closedWithEscape.current = false;
    return () => {
      if (closedWithEscape.current && opener.current?.isConnected) opener.current.focus();
    };
  }, [isOpen]);

  // The panel is hidden until it's positioned; move focus into it as soon as it shows. The panel itself takes
  // it (no ring); Tab goes on to the search field.
  const shown = style.visibility !== "hidden";
  useEffect(() => {
    if (isOpen && shown) panelRef.current?.focus();
  }, [isOpen, shown, panelRef]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element;
      // Menus and the confirm dialog open from inside the panel but render outside it.
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      if (target.closest('[role="menu"], [role="dialog"]')) return;
      onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !document.querySelector('[role="menu"], [aria-modal="true"]')) {
        closedWithEscape.current = true;
        onClose();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, panelRef, anchorRef]);

  const searching = query.trim() !== "";
  const flat = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    return trimmed ? spaces.filter((sp) => sp.name.toLowerCase().includes(trimmed)) : spaces;
  }, [spaces, query]);

  if (!isOpen) return null;

  const open = (key: string) => {
    onOpenSpace(key);
    onClose();
  };

  const flatRow = (space: SidebarNavSpaceItem) => {
    const current = folders.find((f) => f.spaceKeys.includes(space.key));
    const items: ContextMenuItem[] = [
      {
        label: space.pinned ? unpinLabel : starLabel,
        icon: <Star size={14} />,
        onSelect: () => onTogglePinSpace(space.key),
      },
      ...(handlers.onOpenSpaceSettings
        ? [
            {
              label: labels.spaceSettings,
              icon: <Settings size={14} />,
              onSelect: () => handlers.onOpenSpaceSettings?.(space.key),
            },
          ]
        : []),
      ...folders
        .filter((f) => f.key !== current?.key)
        .map((f) => ({
          label: fill(labels.moveTo, f.name),
          icon: <Folder size={14} />,
          group: labels.moveToGroup,
          onSelect: () => handlers.onMoveSpace(space.key, f.key, null),
        })),
      ...(current
        ? [
            {
              label: labels.removeFromFolder,
              icon: <FolderMinus size={14} />,
              group: "",
              onSelect: () => handlers.onRemoveFromFolder(space.key),
            },
          ]
        : []),
    ];
    return (
      <div key={space.key}>
        <SidebarSpaceRow
          space={space}
          detail={current?.name}
          showStar={false}
          active={space.key === activeSpaceKey}
          starLabel={space.pinned ? unpinLabel : starLabel}
          onOpen={() => open(space.key)}
          onToggleStar={() => onTogglePinSpace(space.key)}
          menu={{ label: labels.spaceMenu, ariaLabel: fill(labels.spaceMenuAria, space.name), items }}
        />
      </div>
    );
  };

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={title}
      tabIndex={-1}
      style={style}
      className="z-[110] flex max-h-[70vh] w-72 flex-col rounded-xl border border-border-subtle bg-surface-sidebar p-2 shadow-lg outline-none"
    >
      <div className="flex items-center justify-between py-1 pl-3 pr-2">
        <span className="[font:var(--type-eyebrow)] uppercase tracking-wide text-content-tertiary">{title}</span>
        {browse === "folders" && (
          <div className="flex items-center gap-1">
            <Tooltip content={labels.newFolder}>
              <IconButton
                icon={<FolderPlus />}
                aria-label={labels.newFolder}
                variant="ghost"
                size="sm"
                onClick={() => setNewFolderRequest((n) => n + 1)}
              />
            </Tooltip>
            {onNewSpace && newSpaceLabel && (
              <Tooltip content={newSpaceLabel}>
                <IconButton
                  icon={<Plus />}
                  aria-label={newSpaceLabel}
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    onNewSpace();
                    onClose();
                  }}
                />
              </Tooltip>
            )}
          </div>
        )}
      </div>
      <div className="pt-1 pb-2">
        <Input
          size="sm"
          leadingIcon={<Search />}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={labels.searchSpaces}
          aria-label={labels.searchSpaces}
        />
      </div>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
        {searching || browse === "list" ? (
          flat.length > 0 ? (
            flat.map(flatRow)
          ) : (
            <EmptyNote icon={spaces.length === 0 ? <Box /> : <Search />}>
              {spaces.length === 0 ? labels.noSpaces : labels.noResults}
            </EmptyNote>
          )
        ) : (
          <SidebarSpaceFolders
            spaces={spaces}
            looseSpaces={looseSpaces}
            folders={folders}
            handlers={handlers}
            labels={labels}
            activeSpaceKey={activeSpaceKey}
            onOpenSpace={open}
            onTogglePinSpace={onTogglePinSpace}
            requestNewFolder={newFolderRequest}
            starLabel={starLabel}
            unpinLabel={unpinLabel}
            onNewSpace={onNewSpace}
            newSpaceLabel={newSpaceLabel}
            showStar={false}
          />
        )}
      </div>
      {onViewPage && (
        <div className="mt-2 border-t border-border-subtle pt-2">
          <Button
            variant="ghostNeutral"
            size="sm"
            fullWidth
            onClick={() => {
              onViewPage();
              onClose();
            }}
          >
            {labels.viewAllPage}
          </Button>
        </div>
      )}
    </div>,
    document.body,
  );
}
